import { MediaType, ExternalAssetSource } from "../../types";
import { BaseProvider } from "../base/BaseProvider";
import { IAsset, SearchParams } from "../base/types";
import { ImageAsset, VideoAsset } from "./assets";
import { PixabayImageItem, PixabaySearchResponse, PixabayVideoItem } from "./types";

interface PixabayProviderConfig {
  /** Pixabay API key from https://pixabay.com/api/docs/ */
  apiKey: string;
}

/**
 * Pixabay Provider
 *
 * Provides read-only access to Pixabay's library of free images and videos.
 * https://pixabay.com/
 *
 * API documentation: https://pixabay.com/api/docs/
 * Supports both image search and video search.
 */
export class PixabayProvider extends BaseProvider {
  private static readonly IMAGES_API = 'https://pixabay.com/api/';
  private static readonly VIDEOS_API = 'https://pixabay.com/api/videos/';
  private static readonly CATEGORIES = ['backgrounds', 'fashion', 'nature', 'science', 'education', 'feelings', 'health', 'people', 'religion', 'places', 'animals', 'industry', 'computer', 'food', 'sports', 'transportation', 'travel', 'buildings', 'business', 'music'];

  constructor(config: PixabayProviderConfig) {
    super({
      id: ExternalAssetSource.Pixabay,
      name: 'pixabay',
      displayName: 'Pixabay',
      description: 'Free images and videos from Pixabay',
      baseUrl: PixabayProvider.IMAGES_API,
      apiKey: config.apiKey,
      capabilities: {
        read: true,
        write: false,
        delete: false,
        supportedTypes: [MediaType.Image, MediaType.Video],
      },
    });
  }

  /**
   * Returns the list of predefined Pixabay categories.
   * Used by ExploreView to populate the category filter sidebar.
   */
  getCategories(): Promise<string[]> {
    return Promise.resolve([...PixabayProvider.CATEGORIES]);
  }

  /**
   * Map our sort string to Pixabay's order param value.
   * Pixabay accepts 'popular' (default) or 'latest'.
   */
  private mapSort(sort?: string): string {
    return sort === 'latest' ? 'latest' : 'popular';
  }

  /**
   * Search for assets on Pixabay.
   * Routes to the images API or videos API based on the requested media type.
   * If types contains both Image and Video, returns results from both.
   */
  async search(params: SearchParams): Promise<IAsset[]> {
    const requestedTypes = params.types ?? [MediaType.Image, MediaType.Video];
    const wantsImages = requestedTypes.includes(MediaType.Image);
    const wantsVideos = requestedTypes.includes(MediaType.Video);

    const results: IAsset[] = [];

    if (wantsImages) {
      results.push(...await this.searchImages(params));
    }
    if (wantsVideos) {
      results.push(...await this.searchVideos(params));
    }

    return results;
  }

  private async searchImages(params: SearchParams): Promise<IAsset[]> {
    const url = new URL(PixabayProvider.IMAGES_API);
    url.searchParams.set('key', this.apiKey!);
    url.searchParams.set('page', String(params.page ?? 1));
    url.searchParams.set('per_page', String(params.limit ?? 20));
    url.searchParams.set('order', this.mapSort(params.sort));
    if (params.query) url.searchParams.set('q', params.query);
    if (params.category) url.searchParams.set('category', params.category);

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`Pixabay image search failed: ${response.statusText}`);
    const data = await response.json() as PixabaySearchResponse<PixabayImageItem>;
    return data.hits.map(item => new ImageAsset(item));
  }

  private async searchVideos(params: SearchParams): Promise<IAsset[]> {
    const url = new URL(PixabayProvider.VIDEOS_API);
    url.searchParams.set('key', this.apiKey!);
    url.searchParams.set('page', String(params.page ?? 1));
    url.searchParams.set('per_page', String(params.limit ?? 20));
    url.searchParams.set('order', this.mapSort(params.sort));
    if (params.query) url.searchParams.set('q', params.query);
    if (params.category) url.searchParams.set('category', params.category);

    const response = await fetch(url.toString());
    if (!response.ok) throw new Error(`Pixabay video search failed: ${response.statusText}`);
    const data = await response.json() as PixabaySearchResponse<PixabayVideoItem>;
    return data.hits.map(item => new VideoAsset(item));
  }

  /**
   * Get a specific asset by ID.
   * Pixabay uses pageURL as the id — we try images first, then videos.
   */
  async getAssetById(id: string): Promise<IAsset> {
    // The id is the pageURL; extract the numeric Pixabay ID from it
    // pageURL looks like: https://pixabay.com/photos/forest-road-trees-12345/
    const match = id.match(/(\d+)\/?$/);
    if (!match) throw new Error(`Invalid Pixabay asset ID: ${id}`);

    const numericId = match[1];

    // Try image endpoint first
    try {
      const url = new URL(PixabayProvider.IMAGES_API);
      url.searchParams.set('key', this.apiKey!);
      url.searchParams.set('id', numericId);
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Pixabay image lookup failed: ${response.statusText}`);
      const data = await response.json() as PixabaySearchResponse<PixabayImageItem>;
      if (data.hits.length > 0) return new ImageAsset(data.hits[0]);
    } catch { /* fall through to try videos */}

    // Try video endpoint
    try {
      const url = new URL(PixabayProvider.VIDEOS_API);
      url.searchParams.set('key', this.apiKey!);
      url.searchParams.set('id', numericId);
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`Pixabay video lookup failed: ${response.statusText}`);
      const data = await response.json() as PixabaySearchResponse<PixabayVideoItem>;
      if (data.hits.length > 0) return new VideoAsset(data.hits[0]);
    } catch { /* fall through */ }

    throw new Error(`Asset with ID ${id} not found in Pixabay`);
  }
}
